import { BAGGAGE_FEATURE_NAMES, BRANDED_FARE_FEATURE_ICON_COMPONENTS, BRANDED_FARE_FEATURES_CONFIG, CHANGEABLE_TICKET_FEATURES, REFUNDABLE_TICKET_FEATURES } from "@flights/branded-fares";
import flights_apex_web_filter_align_branded_fare_features_apps from "../../utils/experiments/apex/flights_apex_web_filter_align_branded_fare_features_apps.js";
const boardingAndFlightCategories = ["CHECK_IN", "CS", "FEE", "DETAILS", "BAGGAGE"];
const baggageFeatureNames = Object.values(BAGGAGE_FEATURE_NAMES);
export const getBoardingAndFlightFeatures = (features) => features?.filter((feature) => feature.category &&
    boardingAndFlightCategories.includes(feature.category) &&
    feature.availability !== "NOT_INCLUDED" &&
    !baggageFeatureNames.includes(feature.featureName));
export const getFeaturesToRender = (brandedFareInfo, options = {}) => {
    const useAlignedFeatureContent = shouldUseAlignedFeatureContent();
    const { included, sellable, nonIncluded } = getFeaturesByAvailability(brandedFareInfo, useAlignedFeatureContent);
    const includedFeatures = filterRenderableFeatures(included, sellable, options, useAlignedFeatureContent);
    const nonIncludedFeatures = filterRenderableFeatures(nonIncluded, [], options, useAlignedFeatureContent);
    const nonBaggageFeatures = includedFeatures.filter((f) => !baggageFeatureNames.includes(f.featureName));
    return { includedFeatures, nonIncludedFeatures, nonBaggageFeatures };
};
const shouldUseAlignedFeatureContent = () => process.env.BUILD_TARGET === "client" &&
    Boolean(flights_apex_web_filter_align_branded_fare_features_apps.trackWithDefaultStage());
const getFeaturesByAvailability = (brandedFareInfo, useAlignedFeatureContent) => {
    if (useAlignedFeatureContent) {
        return {
            included: getWithAvailability(brandedFareInfo.featuresList, "INCLUDED"),
            sellable: getWithAvailability(brandedFareInfo.featuresList, "SELLABLE"),
            nonIncluded: getWithAvailability(brandedFareInfo.featuresList, "NOT_INCLUDED")
        };
    }
    return {
        included: getWithAvailability(brandedFareInfo.features, "INCLUDED"),
        sellable: getWithAvailability(brandedFareInfo.sellableFeatures, "SELLABLE"),
        nonIncluded: getWithAvailability(brandedFareInfo.nonIncludedFeatures, "NOT_INCLUDED")
    };
};
export const mergeFeaturesInOrder = (a, b) => {
    const source = [...a, ...b];
    return BRANDED_FARE_FEATURES_CONFIG.flatMap((config) => findFeatures(config, source));
};
const filterRenderableFeatures = (features, alternativeFeatures = [], options = {}, useAlignedFeatureContent = false) => {
    const compressed = compressSimilarFeatures(features);
    const alternativeCompressed = compressSimilarFeatures(alternativeFeatures);
    const renderableFeatures = [];
    for (const config of BRANDED_FARE_FEATURES_CONFIG) {
        const { group } = config;
        let foundFeatures = findFeatures(config, compressed);
        if (foundFeatures.length === 0)
            foundFeatures = findFeatures(config, alternativeCompressed);
        for (const found of foundFeatures) {
            const label = getFeatureLabel(found, useAlignedFeatureContent);
            if (!label)
                continue;
            const { availability } = found;
            if (availability === "SELLABLE" && !shouldShowSellableFeature(options, config))
                continue;
            if (availability === "NOT_INCLUDED" && !shouldShowExcludedFeature(found, options, group))
                continue;
            renderableFeatures.push({
                ...found,
                label,
                group,
                icon: getFeatureIcon(found, config, useAlignedFeatureContent)
            });
        }
    }
    return renderableFeatures;
};
const getFeatureLabel = (feature, useAlignedFeatureContent) => {
    if (!useAlignedFeatureContent)
        return feature.label;
    return getFeatureContent(feature).label;
};
const getFeatureIcon = (feature, config, useAlignedFeatureContent) => {
    if (!useAlignedFeatureContent)
        return config.icon;
    const iconName = getFeatureContent(feature).icon;
    return BRANDED_FARE_FEATURE_ICON_COMPONENTS[iconName];
};
const getFeatureContent = (feature) => feature.content;
const shouldShowSellableFeature = (options, config) => {
    const { group, name } = config;
    if (group === "FLEXIBILITY" && !options.isTabularBFVariant)
        return true;
    if (name === "LOUNGE_ACCESS")
        return true;
    return options.includeSellableFeatures && options.isPreCheckAndPay;
};
const shouldShowExcludedFeature = (feature, options, group) => group === "FLEXIBILITY" ||
    (options.includeExcludedFeatures && (group !== "BAGGAGE" || options.isPreCheckAndPay)) ||
    feature.featureName === "CHECK_BAGGAGE";
const getWithAvailability = (features, availability) => (features || []).filter((f) => f.availability === availability);
const findFeatures = (config, features) => features.filter((f) => f.featureName === config.name);
const compressSimilarFeatures = (features) => {
    const compressedFeatures = [];
    let changeable = false;
    let refundable = false;
    features.forEach((feature) => {
        const { featureName } = feature;
        if (CHANGEABLE_TICKET_FEATURES.includes(featureName)) {
            if (!changeable) {
                compressedFeatures.push(feature);
                changeable = true;
            }
        }
        else if (REFUNDABLE_TICKET_FEATURES.includes(featureName)) {
            if (!refundable) {
                compressedFeatures.push(feature);
                refundable = true;
            }
        }
        else {
            compressedFeatures.push(feature);
        }
    });
    return compressedFeatures;
};
